// src/pages/AllProfiles.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/AllProfiles.css";
import { getJson } from "../network/api";
import { ApiEndpoints } from "../network/config";
import { useLocation } from "react-router-dom";

const AllProfiles = () => {
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("userId");
  const [profiles, setProfiles] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    limit: 20,
    total: 0,
  });

  // Filters state
  const [filters, setFilters] = useState({
    fromAge: "",
    toAge: "",
    caste: "",
    education: "",
  });
  const [ageError, setAgeError] = useState("");
  const [pageInput, setPageInput] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const location = useLocation();

  // -----------------------------------
  // Helper: get profile image URL safely
  // -----------------------------------
  const getProfileImageUrl = (p) => {
    const profileImages = Array.isArray(p.profileImages) ? p.profileImages : [];
    const firstImageObj = profileImages.length > 0 ? profileImages[0] : null;

    const candidates = [
      firstImageObj &&
      (firstImageObj.imageUrl || firstImageObj.url || firstImageObj.image),
      p.imageUrl,
      p.profileImageUrl,
      p.profileImage,
    ];

    for (const c of candidates) {
      if (typeof c === "string" && c.trim() !== "") {
        return c;
      }
    }
    return "";
  };

  const getSgsNo = (p) => p.sgsNo || p.sgsno || p.sgs_number || p.sgs || "";

  const getFileNo = (p) => p.fileNo || p.file_no || p.file || "";

  const loadProfiles = async (page = 1) => {
    try {
      setLoading(true);
      setError("");

      const { fromAge, toAge, caste, education } = filters;

      const response = await getJson(ApiEndpoints.allProfiles, {
        params: {
          page: page,
          limit: 20,
          appUserId: userId,
          fromAge,
          toAge,
          caste,
          education,
        },
      });

      const records =
        (response &&
          (response.data?.records ||
            response.data?.profiles ||
            response.data ||
            response.records ||
            response.profiles)) ||
        [];

      setProfiles(Array.isArray(records) ? records : []);

      if (response.pagination_data) {
        setPagination(response.pagination_data);
      } else {
        setPagination((prev) => ({
          ...prev,
          page,
          total: Array.isArray(records) ? records.length : 0,
          pages: 1,
        }));
      }
    } catch (err) {
      console.error("API Error:", err);
      setError("Failed to load profiles.");
      setProfiles([]);
      setPagination((prev) => ({ ...prev, page: 1, pages: 1, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // initial load (no filters)
  useEffect(() => {
    const savedPage =
      location.state?.page ||
      sessionStorage.getItem("allProfilesPage") ||
      1;

    loadProfiles(Number(savedPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------
  // Filter handlers
  // -----------------------------------
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateAgeRange = () => {
    const { fromAge, toAge } = filters;

    if ((fromAge && !toAge) || (!fromAge && toAge)) {
      setAgeError("Please select both From Age and To Age.");
      return false;
    }

    if (fromAge && toAge && Number(fromAge) >= Number(toAge)) {
      setAgeError("From Age must be less than To Age.");
      return false;
    }

    setAgeError("");
    return true;
  };

  const handleSearch = () => {
    if (!validateAgeRange()) return;
    // always go back to page 1 when applying filters
    loadProfiles(1);
  };

  // -----------------------------------
  // Pagination handlers
  // -----------------------------------
  const handleFirst = () => loadProfiles(1);

  const handlePrev = () => {
    if (pagination.page > 1) {
      loadProfiles(pagination.page - 1);
    }
  };

  const handleNext = () => {
    if (pagination.page < pagination.pages) {
      loadProfiles(pagination.page + 1);
    }
  };

  const handleLast = () => loadProfiles(pagination.pages);

  const handlePageJump = () => {
    const pageNum = Number(pageInput);

    if (!pageNum || pageNum < 1 || pageNum > pagination.pages) return;

    loadProfiles(pageNum);
  };

  const getPageNumbers = () => {
    const totalPages = pagination.pages;
    const current = pagination.page;
    const delta = 2; // pages around current

    const pages = [];

    const rangeStart = Math.max(2, current - delta);
    const rangeEnd = Math.min(totalPages - 1, current + delta);

    pages.push(1);

    if (rangeStart > 2) {
      pages.push("...");
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (rangeEnd < totalPages - 1) {
      pages.push("...");
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="allprofiles-container">
      <div className="header-section">
        <div className="header-left">
          <h1 className="main-title">All Profiles</h1>
        </div>
      </div>

      {/* FILTERS */}
      <div className="filters">
        {/* From Age */}
        <select
          value={filters.fromAge}
          onChange={(e) => handleFilterChange("fromAge", e.target.value)}
        >
          <option value="">From Age</option>
          {Array.from({ length: 33 }, (_, i) => 18 + i).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>

        {/* To Age (with error under it) */}
        <div className="to-age-wrapper">
          <select
            value={filters.toAge}
            onChange={(e) => handleFilterChange("toAge", e.target.value)}
          >
            <option value="">To Age</option>
            {Array.from({ length: 33 }, (_, i) => 18 + i).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          {ageError && <p className="error-text">{ageError}</p>}
        </div>

        {/* Caste */}
        <select
          value={filters.caste}
          onChange={(e) => handleFilterChange("caste", e.target.value)}
        >
          <option value="">Select Caste</option>
          <option value="Hindu - Jangama">Hindu - Jangama</option>
          <option value="Hindu - Lingayatha">Hindu - Lingayatha</option>
          <option value="Hindu - Sadu Lingayath">Hindu - Sadu Lingayath</option>
          <option value="Second Marriage">Second Marriage</option>
        </select>

        {/* Education */}
        <select
          value={filters.education}
          onChange={(e) => handleFilterChange("education", e.target.value)}
        >
          <option value="">Select Education</option>
          <option value="ENGINEERING">Engineering</option>
          <option value="MEDICAL">Medical</option>
          <option value="NURSING">Nursing</option>
          <option value="COMMERCE">Commerce</option>
          <option value="ARTS">Arts</option>
          <option value="SCIENCE">Science</option>
          <option value="DIPLOMA">Diploma</option>
          <option value="PUC">PUC</option>
          <option value="SSLC">SSLC</option>
          <option value="NOT APPLICABLE">Not Applicable</option>
        </select>

        {/* Search button */}
        <button className="search-btn" type="button" onClick={handleSearch}>
          Search
        </button>
      </div>

      {/* Loading & Error UI */}
      {loading && (
        <p style={{ textAlign: "center", marginTop: "30px" }}>Loading...</p>
      )}

      {error && !loading && (
        <p style={{ color: "red", textAlign: "center" }}>{error}</p>
      )}

      {/* PROFILES GRID */}
      {!loading && !error && (
        <div className="profiles-grid">
          {profiles.length === 0 ? (
            <div className="no-results">No profiles found.</div>
          ) : (
            profiles.map((p, index) => {
              const imageUrl = getProfileImageUrl(p);
              const sgsNo = getSgsNo(p);
              const fileNo = getFileNo(p);

              return (
                <div key={p.id ?? p.fileNo ?? index} className="profile-card">
                  <div className="profile-image-wrapper">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={sgsNo || fileNo || "Profile"}
                        className="profile-image"
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          console.error("IMAGE FAILED:", imageUrl);
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="profile-placeholder">No Image</div>
                    )}
                  </div>

                  <div className="profile-info">
                    <p className="profile-file">File No: {fileNo || "-"}</p>
                    <p className="profile-sgs">SGS No: {sgsNo || "-"}</p>

                    <button
                      className="more-info"
                      onClick={() => {
                        sessionStorage.setItem("allProfilesPage", pagination.page);
                        navigate("/all-profile-info", {
                          state: { profileId: p.id },
                          page: pagination.page,
                        });
                      }}
                    >
                      More Info
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* PAGINATION BAR */}
      {!loading && !error && (

        <div className="pagination-bar">

          {/* -------- ROW 1: NUMBERED PAGINATION -------- */}
          <div className="pagination-row">

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(1)}
              disabled={pagination.page === 1}
            >
              &#171;
            </button>

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              &#8592;
            </button>

            {getPageNumbers().map((p, index) =>
              p === "..." ? (
                <span key={index} className="pagination-dots">...</span>
              ) : (
                <button
                  key={p}
                  className={`pagination-btn ${p === pagination.page ? "active" : ""
                    }`}
                  onClick={() => loadProfiles(p)}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
            >
              &#8594;
            </button>

            <button
              className="pagination-btn"
              onClick={() => loadProfiles(pagination.pages)}
              disabled={pagination.page === pagination.pages}
            >
              &#187;
            </button>

          </div>

          {/* -------- ROW 2: PAGE JUMP -------- */}
          <div className="pagination-row jump-row">
            <span>Go to page:</span>

            <input
              type="number"
              min="1"
              max={pagination.pages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              className="page-input"
            />

            <button className="pagination-btn" onClick={handlePageJump}>
              Go
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

export default AllProfiles;
